/**
 * Porta de `mobileapp_v2/src/screens/ExtraActivitiesScreen` para manter web e
 * app com a mesma organização.
 *
 * A API devolve Tipo > Ano > Semestre > mídias. Na tela exibimos:
 *   - Workbook como seção própria (não muda por ano/semestre);
 *   - demais tipos reagrupados em Ano > Semestre > Tipo > mídias.
 *
 * Tudo defensivo contra nulos, como no app.
 */

const WORKBOOK_NAME_MATCH = 'workbook';

const compact = list => (list || []).filter(Boolean);

// Array.prototype.flatMap sem polyfill no CRA 3 — navegadores antigos não têm.
const flatMap = (list, mapper) =>
    list.reduce((accumulator, item) => {
        accumulator.push(...mapper(item));
        return accumulator;
    }, []);

const isWorkbookType = activityType =>
    (activityType.name || '').toLowerCase().includes(WORKBOOK_NAME_MATCH);

const flattenMedias = activityType =>
    flatMap(
        flatMap(compact(activityType.years), year => compact(year.semesters)),
        semester => compact(semester.medias)
    );

export const extractWorkbookMedias = activityTypes =>
    flatMap(compact(activityTypes).filter(isWorkbookType), flattenMedias);

const descending = ([keyA], [keyB]) => keyB - keyA;

/**
 * O(n) sobre o total de mídias: um Map por nível de agrupamento.
 */
export const groupByYearAndSemester = activityTypes => {
    const semestersByYear = new Map();

    compact(activityTypes)
        .filter(activityType => !isWorkbookType(activityType))
        .forEach(activityType => {
            compact(activityType.years).forEach(year => {
                if (!semestersByYear.has(year.year)) semestersByYear.set(year.year, new Map());
                const semesterMap = semestersByYear.get(year.year);

                compact(year.semesters).forEach(semester => {
                    if (!semesterMap.has(semester.semester))
                        semesterMap.set(semester.semester, { label: semester.label, typeGroups: new Map() });
                    const { typeGroups } = semesterMap.get(semester.semester);

                    const medias = typeGroups.get(activityType.name) || [];
                    typeGroups.set(activityType.name, medias.concat(compact(semester.medias)));
                });
            });
        });

    return Array.from(semestersByYear.entries())
        .sort(descending)
        .map(([year, semesterMap]) => ({
            year,
            semesters: Array.from(semesterMap.entries())
                .sort(descending)
                .map(([semester, { label, typeGroups }]) => ({
                    semester,
                    label,
                    typeGroups: Array.from(typeGroups.entries())
                        .filter(([, medias]) => medias.length > 0)
                        .map(([activityTypeName, medias]) => ({ activityTypeName, medias }))
                }))
        }));
};
